import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom-v5-compat';

import { useEnablePurchaseOrders } from '../../../feature-flags/hooks/use-enable-purchase-orders';
import { useModal } from '../../../hooks/use-modal';
import { trackCreatePurchaseOrder } from '../utils/reorder-assist-tracking';

/**
 * @typedef {import('../context/forecasting-data-context').InventoryItemFromEntity} InventoryItemFromEntity
 */

/**
 * @typedef {object} ForecastingDataItem
 * @property {string} id - The item ID
 * @property {number} [quantity] - Current quantity
 * @property {number} [suggestedReorder] - Suggested reorder amount
 */

/**
 * @typedef {object} PurchaseOrderIntegrationReturn
 * @property {boolean} isPurchaseOrdersEnabled - Whether purchase orders are enabled
 * @property {boolean} isPurchaseOrderDrawerOpen - Whether the purchase order drawer is open
 * @property {object | null} initializedPurchaseOrder - Initialized purchase order data
 * @property {() => void} handleCreatePurchaseOrderClick - Handler for create purchase order click
 * @property {() => void} closePurchaseOrderDrawer - Handler to close purchase order drawer
 */

/**
 * Hook for managing purchase order integration within the forecasting drawer
 * @param {Array<ForecastingDataItem>} data - The forecasting data
 * @param {{[key: string]: number}} quantityValues - Object containing quantity values by item ID
 * @param {Array<InventoryItemFromEntity>} rawEntities - Raw entity data for product ID mapping
 * @returns {PurchaseOrderIntegrationReturn} Purchase order integration state and handlers
 */
export const usePurchaseOrderIntegration = (data, quantityValues, rawEntities) => {
  const { inventoryWarehouseId } = useParams();
  const isPurchaseOrdersEnabled = useEnablePurchaseOrders();
  const { open: openPurchaseOrderDrawer, close: closePurchaseOrderDrawer, isOpen: isPurchaseOrderDrawerOpen } = useModal();

  const handleCreatePurchaseOrderClick = useCallback(() => {
    if (!isPurchaseOrdersEnabled || data.length === 0) {
      return;
    }
    trackCreatePurchaseOrder(data, quantityValues, inventoryWarehouseId);
    openPurchaseOrderDrawer();
  }, [isPurchaseOrdersEnabled, data, quantityValues, inventoryWarehouseId, openPurchaseOrderDrawer]);

  const initializedPurchaseOrder = useMemo(() => {
    if (!data?.length) {
      return null;
    }

    const productIdMap = rawEntities.reduce((acc, entity) => {
      const productId = entity.productId || entity.productID;
      if (productId) {
        return { ...acc, [entity.id]: productId };
      }
      return acc;
    }, {});

    return {
      selectedProductsWithQuantity: data
        .map((item) => ({
          productId: productIdMap[item.id] || item.id,
          quantity: quantityValues[item.id] ?? item.quantity ?? item.suggestedReorder ?? 1,
        }))
        .filter((item) => item.quantity > 0),
      warehouseId: inventoryWarehouseId || null,
    };
  }, [data, quantityValues, inventoryWarehouseId, rawEntities]);

  return {
    isPurchaseOrdersEnabled,
    isPurchaseOrderDrawerOpen,
    initializedPurchaseOrder,
    handleCreatePurchaseOrderClick,
    closePurchaseOrderDrawer,
  };
};
